// true  = open the cart drawer whenever something is added (product page, Konfigr, quick add)
// false = just update the drawer and header count in the background
window.CART_DRAWER_OPEN_ON_ADD = false;

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.bindOverlay();
    this.setHeaderCartIconAccessibility();

    // Keep the empty/non-empty layout in step when items are removed or quantities changed in the drawer.
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        const count = event && event.cartData && event.cartData.item_count;
        if (typeof count !== 'number') return;
        this.classList.toggle('is-empty', count === 0);
        const inner = this.querySelector('.drawer__inner');
        if (inner) inner.classList.toggle('is-empty', count === 0);
      });
    }
  }

  // Overlay sits inside #CartDrawer, which is replaced on every render, so it is re-bound after each update.
  bindOverlay() {
    const overlay = this.querySelector('#CartDrawer-Overlay');
    if (overlay && !overlay.dataset.bound) {
      overlay.addEventListener('click', this.close.bind(this));
      overlay.dataset.bound = 'true';
    }
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        if (containerToTrapFocusOn) trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState, options = {}) {
    const openAfter = typeof options.open === 'boolean' ? options.open : window.CART_DRAWER_OPEN_ON_ADD === true;
    const inner = this.querySelector('.drawer__inner');
    if (inner && inner.classList.contains('is-empty')) inner.classList.remove('is-empty');
    this.classList.remove('is-empty');
    this.productId = parsedState && parsedState.id;

    const sections = (parsedState && parsedState.sections) || {};
    const complete = this.getSectionsToRender().every((section) => sections[section.id]);

    // If the add-to-cart response didn't include fresh section HTML, fetch it separately
    // rather than failing silently and leaving the page out of date.
    const update = complete ? Promise.resolve(this.applySections(sections)) : this.refresh();

    return update.finally(() => {
      this.bindOverlay();
      if (!openAfter) return;
      setTimeout(() => this.open());
    });
  }

  // Swaps in fresh HTML for each cart section. Skips anything missing instead of throwing.
  applySections(sections) {
    this.syncEmptyState(sections && sections['cart-drawer']);
    let updated = 0;
    this.getSectionsToRender().forEach((section) => {
      const html = sections && sections[section.id];
      const target = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      if (!html || !target) return;
      const source = this.getSectionDOM(html, section.selector);
      if (!source) return;
      target.innerHTML = source.innerHTML;
      updated++;
    });
    return updated;
  }

  // The empty/non-empty layout is controlled by the is-empty class on <cart-drawer> itself,
  // which section re-renders don't touch. Copy it from the freshly rendered HTML.
  syncEmptyState(html) {
    if (!html) return;
    const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('cart-drawer');
    if (!fresh) return;
    const empty = fresh.classList.contains('is-empty');
    this.classList.toggle('is-empty', empty);
    const inner = this.querySelector('.drawer__inner');
    if (inner) inner.classList.toggle('is-empty', empty);
  }

  // Re-fetches the cart sections via Shopify's Section Rendering API.
  // Falls back to just updating the header count from /cart.js if that fails.
  refresh() {
    const ids = this.getSectionsToRender()
      .map((section) => section.id)
      .join(',');
    return fetch(`${window.location.pathname}?sections=${ids}`)
      .then((response) => (response.ok ? response.json() : Promise.reject(response.status)))
      .then((sections) => {
        if (!this.applySections(sections)) return this.updateBubbleCount();
        this.bindOverlay();
      })
      .catch((e) => {
        console.error('Cart drawer refresh failed', e);
        return this.updateBubbleCount();
      });
  }

  updateBubbleCount() {
    return fetch(`${window.routes.cart_url}.js`)
      .then((response) => response.json())
      .then((cart) => {
        const icon = document.querySelector('#cart-icon-bubble');
        if (!icon) return;
        let bubble = icon.querySelector('.cart-count-bubble');
        if (cart.item_count === 0) {
          if (bubble) bubble.remove();
          return;
        }
        if (!bubble) {
          bubble = document.createElement('div');
          bubble.className = 'cart-count-bubble';
          bubble.innerHTML = '<span aria-hidden="true"></span><span class="visually-hidden"></span>';
          icon.appendChild(bubble);
        }
        const visible = bubble.querySelector('[aria-hidden="true"]');
        const hidden = bubble.querySelector('.visually-hidden');
        if (visible) visible.textContent = cart.item_count < 100 ? cart.item_count : '';
        if (hidden) hidden.textContent = `${cart.item_count} items`;
      })
      .catch((e) => console.error('Cart count update failed', e));
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const element = this.getSectionDOM(html, selector);
    return element ? element.innerHTML : '';
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    if (!html) return null;
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

if (!customElements.get('cart-drawer')) customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

if (!customElements.get('cart-drawer-items')) customElements.define('cart-drawer-items', CartDrawerItems);
